// Catch odds. A species' price sets how hard it is to bind; a seal's
// efficiency multiplies the chance; a fallen body is easier than a living one.
import { firstHuntLevel } from './grounds.ts';
import { SPECIES, type Rarity, type RegionId } from './species.ts';

export const CATCH = { base: 0.0075, rarityDiv: 17, floor: 0.003, cap: 0.1 } as const;

/** Reference price by rarity tier, in coins. */
export const RARITY_PRICE: Record<Rarity, number> = { common: 100, uncommon: 1000, rare: 5000, epic: 18_000, legendary: 50_000 };

/**
 * Catch prices count a species' first hunt from this level at least: where each region opened before the regions
 * overlapped (2026-10-20), so a region's early hunts keep their catch odds and a later region's species stay pricier
 * than an earlier one's.
 */
const PRICE_FROM: Partial<Record<RegionId, number>> = { hellas: 150, yomi: 900 };
/**
 * Midgard species first met at this hunt level or later are priced on the same level curve (Cole 2026-10-29: with a
 * Mythril Seal every Midgard species caught at the tier price of its line, so a Lv 50 Muninraven bound at 4% a throw,
 * about 3x his pokeidle log). pokeidle prices each species and its evolved forms higher, which the curve stands in for.
 * The first hunts keep the tier price, so a new trainer's first catches are unchanged.
 */
export const MIDGARD_CURVE_FROM = 20;
const priceCurve = (lvl: number) => Math.round(12_000 * Math.pow(lvl / 150, 0.6));

/** Price of a species for catch odds: its tier price, raised by the hunt it is first met in (Midgard from MIDGARD_CURVE_FROM, every Hellas and Yomi species). */
export function speciesPrice(speciesId: string): number {
  const sp = SPECIES[speciesId];
  const tier = RARITY_PRICE[sp.rarity];
  const lvl = firstHuntLevel(speciesId);
  if (lvl === undefined) return tier;
  if (sp.region === 'midgard') return lvl >= MIDGARD_CURVE_FROM ? Math.max(tier, priceCurve(lvl)) : tier;
  const from = PRICE_FROM[sp.region];
  if (from === undefined) return tier;
  return Math.max(tier, priceCurve(Math.max(lvl, from)));
}

/**
 * Chance one seal binds the creature.
 * rarity = log10(max(100, price)) - 1; factor = 1 + rarity³/17;
 * chance = clamp(0.0075 × (efficiency / factor) × (1.4 - hpFrac) × bonus, 0.3%, 10%).
 * hpFrac is 0 for a fallen body.
 */
export function catchChance(price: number, efficiency: number, hpFrac = 0, bonus = 1): number {
  const rarity = Math.log10(Math.max(100, price)) - 1;
  const factor = 1 + Math.pow(rarity, 3) / CATCH.rarityDiv;
  const raw = CATCH.base * (efficiency / factor) * (1.4 - hpFrac) * bonus;
  return Math.min(CATCH.cap, Math.max(CATCH.floor, raw));
}

export function speciesCatchChance(speciesId: string, efficiency: number, bonus = 1): number {
  return catchChance(speciesPrice(speciesId), efficiency, 0, bonus);
}
